import React, { useRef, useState, useEffect } from 'react';

import { useSelector } from 'react-redux';

import classNames from 'classnames';
import { addYears, startOfYear, addMonths, subYears, getDaysInMonth, getYear } from 'date-fns';

import Image from 'next/image';

import { DATE_LABEL, FUTURE_YEARS, MONTH, MONTH_LABEL, PAST_YEARS, YEAR_LABEL } from '@/constants/date';
import { getMinAgeDate, format, currentNYTime, parse } from '@/helpers/dateUtil';

import { ResourceString } from '@/reducers/common/commonTypes';
import { selectResources } from '@/reducers/common/selectors';

import styles from './DateOfBirth.module.scss';

type DateOfBirthProps = {
  bookedAccount?: number;
  dobValue?: string;
  serverISOTime?: string;
  onChangeDob?: Function;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  isMinAgeError?: boolean;
  tabIndex?: string | number;
  isShowFutureYears?: boolean;
};

const hasValue = (selection: any) =>
  !!selection && ['dd', 'mmm', 'yyyy'].every((template) => !selection.toString().toLowerCase().includes(template));

const formatNumber = (number: number) => (number < 10 ? `0${number}` : number);

const yearOrDefault = (year: any) => (hasValue(year) ? year : 1970);

const DateOfBirth = ({
  bookedAccount,
  serverISOTime = '',
  dobValue = '',
  onChangeDob = () => {},
  className = '',
  disabled = false,
  error = false,
  isMinAgeError = false,
  tabIndex = '0',
  isShowFutureYears = false,
}: DateOfBirthProps) => {
  const [day, setDay] = useState('DD');
  const [month, setMonth] = useState('MMM');
  const [year, setYear] = useState('YYYY');
  const monthValue = useRef<HTMLSelectElement>(null);
  const dayValue = useRef<HTMLSelectElement>(null);
  const yearValue = useRef<HTMLSelectElement>(null);

  const resources = useSelector(selectResources) as ResourceString;

  const setDob = (date: string) => {
    if (hasValue(date)) {
      const data = parse(date);
      if (data) {
        setDay(data.getDate().toString());
        setMonth(data.getMonth().toString());
        setYear(data.getFullYear().toString());
      }
    }
  };

  const getDob = (dob: string) => ({
    key: 'dob',
    value: dob,
  });

  useEffect(() => {
    if (bookedAccount === 0 && dobValue) {
      setDob(dobValue);
    }
  }, [bookedAccount, dobValue]);

  const getMonthOptions = (currentDate: Date) => {
    let year1 = startOfYear(currentDate);
    const months = [
      <option key="disabledMonth" disabled value="MMM">
        {MONTH_LABEL}
      </option>,
    ];
    for (let x = 0; x < MONTH; x += 1) {
      months.push(
        <option key={`month-${x}`} value={x}>
          {format(year1, 'MMM')?.toUpperCase()}
        </option>,
      );
      year1 = addMonths(year1, 1);
    }
    return months;
  };

  const getFutureYearOptions = (currentDate: Date) => {
    let year1 = startOfYear(currentDate);
    const years = [
      <option key="disabledYear" disabled value="YYYY">
        {YEAR_LABEL}
      </option>,
    ];

    for (let x = 0; x < FUTURE_YEARS; x += 1) {
      years.push(
        <option key={`year-${x}`} value={getYear(year1)}>
          {format(year1, 'yyyy')}
        </option>,
      );

      year1 = addYears(year1, 1);
    }

    return years;
  };

  const getYearOptions = (currentDate: Date) => {
    let year1 = startOfYear(getMinAgeDate(currentDate));
    const years = [
      <option key="disabledYear" disabled value="YYYY">
        {YEAR_LABEL}
      </option>,
    ];
    for (let x = 0; x < PAST_YEARS; x += 1) {
      years.push(
        <option key={`year-${x}`} value={getYear(year1)}>
          {format(year1, 'yyyy')}
        </option>,
      );

      year1 = subYears(year1, 1);
    }

    return years;
  };

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>, type: string) => {
    const { value } = event.target;
    if (type === 'month') {
      setMonth(parseInt(value, 10).toString());
    } else if (type === 'day') {
      setDay(parseInt(value, 10).toString());
    } else if (type === 'year') {
      setYear(parseInt(value, 10).toString());
    }

    const monthN = formatNumber(parseInt(monthValue?.current?.value || '0', 10) + 1);
    const dayN = formatNumber(parseInt(dayValue?.current?.value || '0', 10));
    const dob = `${yearValue?.current?.value}-${monthN}-${dayN}`;

    if (hasValue(monthN) && hasValue(dayN) && hasValue(dob)) {
      onChangeDob(getDob(dob));
    }
  };

  const getDayOptions = () => {
    const days = [
      <option key="disabledDay" disabled value="DD">
        {DATE_LABEL}
      </option>,
    ];
    if (hasValue(month)) {
      const daysInMonth = getDaysInMonth(new Date(yearOrDefault(year), parseInt(month, 10)));
      for (let x = 1; x <= daysInMonth; x += 1) {
        days.push(
          <option key={`day-${x}`} value={x}>
            {x && `0${x}`.slice(-2)}
          </option>,
        );
      }
    } else {
      for (let x = 1; x <= 31; x += 1) {
        days.push(
          <option key={`day-${x}`} value={x}>
            {x && `0${x}`.slice(-2)}
          </option>,
        );
      }
    }
    return days;
  };

  const classes = classNames({
    [styles.dob]: true,
    [className]: true,
  });

  const inputClasses = classNames({
    [styles['input-field']]: true,
    [styles.error]: error || isMinAgeError,
  });

  const currentDate = currentNYTime(serverISOTime);

  const selectDMYComponent = ({
    type,
    value,
    ref,
    options,
    ariaLabel,
  }: {
    type: string;
    value: string;
    ref: React.RefObject<HTMLSelectElement | null>;
    options: React.ReactNode[];
    ariaLabel: string;
  }) => (
    <div className={styles['textfield-input']}>
      <select
        className={inputClasses}
        onChange={(e) => onChange(e, type)}
        value={value}
        required
        ref={ref}
        disabled={disabled}
        tabIndex={tabIndex as number}
        aria-label={ariaLabel}
      >
        {options}
      </select>
      <div className={styles.image}>
        {disabled ? (
          <span className={styles.imagelock} />
        ) : (
          <Image src="/auth/v1/static/image/carrot-down.svg" width={24} height={24} alt="" />
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.dobContainer}>
      <div className={classes}>
        <input type="hidden" value={getDob(`${year}-${month}-${day}`).value} onChange={() => {}} />
        {selectDMYComponent({
          type: 'month',
          value: month,
          ref: monthValue,
          options: getMonthOptions(currentDate),
          ariaLabel: 'Month',
        })}
        {selectDMYComponent({
          type: 'day',
          value: day,
          ref: dayValue,
          options: getDayOptions(),
          ariaLabel: 'Day',
        })}
        {selectDMYComponent({
          type: 'year',
          value: year,
          ref: yearValue,
          options: isShowFutureYears ? getFutureYearOptions(currentDate) : getYearOptions(currentDate),
          ariaLabel: 'Year',
        })}
      </div>
      {isMinAgeError && <p className={styles.ageErrorText}>{resources['CreateAccount.validations.age']}</p>}
    </div>
  );
};

export default DateOfBirth;
